import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { IOrder, IOrderValidation } from "../../models/Order.model";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import PaymentCard from "./PaymentCard";

import { IEpcPayment } from "../../models/epcCustomer.model";
import Loading from "../AssetsComponent/loading";
import {
  fetchAllOrdersForAccounts,
  setOrders,
  updateOrderStatus,
  verifyPaymentByAccount,
} from "../../store/slices/orderSlice";
import ScreenshotModal from "./ScreenshotModal";
import UTRModal from "./UtrModal";
import GoodsModal from "./GoodsModal";
import toast from "react-hot-toast";
import PaginationControls from "../pagination/PaginationControls";
import { getAllEpcPayments } from "../../store/slices/epcSlice";

interface PaymentDetails {
  utrNumber: {
    [key: string]: { utrNumber: string; amount: number; isCompleted: boolean };
  };
  paymentScreenshot: {
    [key: string]: {
      paymentScreenshot: string;
      isCompleted: boolean;
      amount: number;
    };
  };
}

type OrderGridProps = {
  loading: boolean;
  search: string;
  view: IOrderValidation;
  paymentType: "order" | "epc";
};

const OrderGrid: React.FC<OrderGridProps> = ({
  loading,
  search,
  view,
  paymentType,
}) => {
  const dispatch: RootDispatch = useDispatch();

  const filteredOrders = useSelector((s: RootState) => s.order.orderPayments);

  const { ordersTotalPages } = useSelector((s: RootState) => s.order);
  const filteredEpcPayments = useSelector(
    (s: RootState) => s?.epc?.epcPayments || [],
  );
  const epcPagination = useSelector(
    (s: RootState) => s?.epc?.epcPaymentsPagination,
  );
  const [isNextLoading, setIsNextLoading] = useState(false);
  const [isPrevLoading, setIsPrevLoading] = useState(false);

  const [page, setCurrentPage] = useState(1);
  const [pageEpcPayments, setCurrentPageEpcPayments] = useState(1);
  const [isScreenshotModalOpen, setIsScreenshotModalOpen] = useState(false);

  const [selectedOrder, setSelectedOrder] = useState<IOrder | null>(null);
  const [selectedEpcPayment, setSelectedEpcPayment] =
    useState<IEpcPayment | null>(null);

  const [isUtrModalOpen, setIsUtrModalOpen] = useState(false);

  const [isGoodsModalOpen, setIsGoodsModalOpen] = useState(false);
  const [payments, setPayments] = useState(true);

  const fetchOrderPayments = useCallback(async () => {
    if (paymentType === "order") {
      dispatch(
        fetchAllOrdersForAccounts({
          page,
          search,
          status: search.trim() ? undefined : view,
          payments,
        }),
      );
    }
    if (paymentType === "epc") {
      dispatch(
        getAllEpcPayments({
          searchQuery: search,
          paymentStatus: search.trim() ? undefined : view,
          page: pageEpcPayments,
        }),
      );
    }
  }, [dispatch, page, search, view, payments, paymentType, pageEpcPayments]);

  useEffect(() => {
    setCurrentPage(1);
    setCurrentPageEpcPayments(1);
  }, [paymentType]);

  useEffect(() => {
    fetchOrderPayments();
  }, [fetchOrderPayments]);

  const handleNextPage = async () => {
    if (page >= ordersTotalPages) return;
    setIsNextLoading(true);
    const nextPage = page + 1;
    setCurrentPage(nextPage);
    setIsNextLoading(false);
  };

  const handlePreviousPage = async () => {
    if (page <= 1) return;
    setIsPrevLoading(true);
    const prevPage = page - 1;
    setCurrentPage(prevPage);
    setIsPrevLoading(false);
  };
  const handleNextPageEpcPayments = async () => {
    if (pageEpcPayments >= epcPagination.totalPages) return;
    setIsNextLoading(true);
    const nextPage = pageEpcPayments + 1;
    setCurrentPageEpcPayments(nextPage);
    setIsNextLoading(false);
  };

  const handlePreviousPageEpcPayments = async () => {
    if (pageEpcPayments <= 1) return;
    setIsPrevLoading(true);
    const prevPage = pageEpcPayments - 1;
    setCurrentPageEpcPayments(prevPage);
    setIsPrevLoading(false);
  };

  const handleViewProofForEpc = useCallback((epcPayment: IEpcPayment) => {
    setSelectedEpcPayment(epcPayment);
    setSelectedOrder(null); // Clear order when viewing EPC proof
    setIsScreenshotModalOpen(true); // Use the same screenshot modal for EPC proofs
  }, []);

  const handleCardClick = useCallback((order: IOrder, e: React.MouseEvent) => {
    if ((e.target as HTMLElement).tagName === "BUTTON") return;
    setSelectedOrder(order);
  }, []);

  const handleUpdateStatus = useCallback(
    async (id: string, status: string) => {
      // Optimistic update
      dispatch(
        setOrders(
          filteredOrders.map((o: IOrder) =>
            o._id === id ? { ...o, orderStatus: status } : o,
          ),
        ),
      );

      try {
        await dispatch(updateOrderStatus({ id, status })).unwrap();
        toast.success(`Order ${status} successfully`);
      } catch (error) {
        // Revert on error
        await fetchOrderPayments();
        toast.error("Failed to update order status. Please try again.");
      }
    },
    [dispatch, fetchOrderPayments, filteredOrders],
  );

  const handleConfirmPayment = useCallback(
    async (type: "utr" | "screenshot", key: string) => {
      if (!selectedOrder) return;

      try {
        const paymentDetails =
          selectedOrder.paymentDetails as unknown as PaymentDetails;
        const utrData = paymentDetails?.utrNumber;
        const screenshotData = paymentDetails?.paymentScreenshot;

        const matchedUtr = utrData && utrData[key] ? [key] : [];
        const matchedScreenshot =
          screenshotData && screenshotData[key] ? [key] : [];

        const payload = {
          orderId: selectedOrder._id,
          utrNumbers: type === "utr" ? matchedUtr : [],
          screenshots: type === "screenshot" ? matchedScreenshot : [],
        };

        if (
          payload.utrNumbers.length === 0 &&
          payload.screenshots.length === 0
        ) {
          toast.error(
            "UTR Number or Screenshot is required to verify payment.",
          );
          return;
        }

        await dispatch(verifyPaymentByAccount(payload)).unwrap();
        await fetchOrderPayments();

        toast.success("Payment verified successfully!");

        // Close modals after successful verification
        setIsUtrModalOpen(false);
        setIsScreenshotModalOpen(false);
      } catch (error) {
        toast.error("Failed to confirm payment. Please try again.");
        throw error; // Re-throw to handle in modal components
      }
    },
    [dispatch, fetchOrderPayments, selectedOrder],
  );
  if (loading) {
    <Loading />;
  }

  const dataToRender =
    paymentType === "order" ? filteredOrders : filteredEpcPayments;
  const isEmpty = dataToRender.length === 0;

  return loading ? (
    <Loading />
  ) : (
    <div className="space-y-8">
      {paymentType === "order" && (
        <div className="flex items-center gap-3">
          {/* Toggle Switch */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setPayments(!payments);
            }}
            className={`relative flex h-7 w-12 items-center rounded-full transition-all duration-300 ${
              payments ? "bg-blue-700" : "bg-slate-300"
            }`}
          >
            <span
              className={`absolute h-5 w-5 rounded-full bg-white shadow-md transition-all duration-300 ${
                payments ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>

          {/* Label */}
          <div className="flex flex-col leading-tight">
            <h2 className="text-sm font-semibold text-slate-800">
              {payments ? "Viewing Payment Orders" : "Viewing All Orders"}
            </h2>

            <p className="text-xs text-slate-500 capitalize">Status: {view}</p>
          </div>
        </div>
      )}
      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, staggerChildren: 0.1 }}
      >
        {paymentType === "order" &&
          filteredOrders.map((order, idx) => (
            <motion.div
              key={order?._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.05 }}
            >
              <PaymentCard
                order={order}
                onPaymentApproveReject={fetchOrderPayments}
                index={idx}
                onCardClick={handleCardClick}
                onUpdateStatus={handleUpdateStatus}
                onViewUtr={(order) => {
                  setSelectedOrder(order);
                  setSelectedEpcPayment(null); // Clear EPC payment when viewing UTR
                  setIsUtrModalOpen(true);
                }}
                onViewScreenshot={(order) => {
                  setSelectedOrder(order!);
                  setSelectedEpcPayment(null); // Clear EPC payment when viewing order screenshots
                  setIsScreenshotModalOpen(true);
                }}
                onViewGoods={(order) => {
                  setSelectedOrder(order!);
                  setIsGoodsModalOpen(true);
                }}
                type="order"
              />
            </motion.div>
          ))}
        {paymentType === "epc" &&
          filteredEpcPayments.map((payment, idx) => (
            <motion.div
              key={payment._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.05 }}
            >
              <PaymentCard
                epcPayment={payment}
                onPaymentApproveReject={fetchOrderPayments}
                index={idx}
                onUpdateStatus={handleUpdateStatus}
                onViewProofForEpc={handleViewProofForEpc}
                type="epc"
              />
            </motion.div>
          ))}
      </motion.div>
      {paymentType === "order" && (
        <div>
          <PaginationControls
            isNextFetching={isNextLoading}
            isPrevFetching={isPrevLoading}
            currentPage={page}
            totalPages={ordersTotalPages}
            onNext={handleNextPage}
            onPrev={handlePreviousPage}
          />
        </div>
      )}
      {paymentType === "epc" && epcPagination && (
        <div>
          <PaginationControls
            isNextFetching={isNextLoading}
            isPrevFetching={isPrevLoading}
            currentPage={pageEpcPayments}
            totalPages={epcPagination.totalPages}
            onNext={handleNextPageEpcPayments}
            onPrev={handlePreviousPageEpcPayments}
          />
        </div>
      )}
      {isEmpty && (
        <div className="text-center py-12">
          <p className="text-gray-500">
            No {paymentType === "order" ? "order" : "EPC"} payments found
            matching your criteria.
          </p>
        </div>
      )}

      <ScreenshotModal
        isOpen={isScreenshotModalOpen}
        onClose={() => {
          setIsScreenshotModalOpen(false);
          setSelectedEpcPayment(null); // Clear EPC payment when closing
          setSelectedOrder(null); // Clear order when closing
        }}
        selectedOrder={selectedOrder}
        selectedEpcPayment={selectedEpcPayment}
        fetchPayments={fetchOrderPayments}
      />

      <UTRModal
        isOpen={isUtrModalOpen}
        onClose={() => setIsUtrModalOpen(false)}
        selectedOrder={selectedOrder}
        onConfirm={handleConfirmPayment}
      />

      <GoodsModal
        isOpen={isGoodsModalOpen}
        onClose={() => setIsGoodsModalOpen(false)}
        selectedOrder={selectedOrder}
      />
    </div>
  );
};

export default OrderGrid;
